import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ICU from 'i18next-icu'
import { defaultLocale, locales, getDirection, type Locale } from '@/i18n/config'
import de from '../../../locales/de.json'
import ar from '../../../locales/ar.json'
import en from '../../../locales/en.json'

// next-intl's call sites use ICU MessageFormat ({count, plural, one {...}}
// and {name, select, ...}) — the audit found ~13 such keys per locale.
// i18next-icu preserves that syntax so the JSON files port unchanged.
void i18n
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
      ar: { translation: ar },
      en: { translation: en },
    },
    fallbackLng: defaultLocale,
    supportedLngs: [...locales],
    nsSeparator: false,
    keySeparator: '.',
    interpolation: { escapeValue: false },
    detection: {
      // localStorage FIRST: it's the only store that survives a hard reload on
      // iOS's capacitor://localhost custom scheme (cookies don't persist there,
      // so on native the old cookie-only setup fell through to `navigator` =
      // the device language). Cookie kept for web/SSR; navigator is last resort.
      // Keys mirror src/lib/locale-persistence.ts.
      order: ['localStorage', 'cookie', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      lookupCookie: 'NEXT_LOCALE',
      caches: ['localStorage', 'cookie'],
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    react: { useSuspense: false },
  })

// Keep <html lang> and <html dir> in sync with the active locale — replaces
// the server-rendered values that the Next root layout used to set.
function syncHtmlAttrs(lang: string) {
  const locale = (locales.includes(lang as Locale) ? lang : defaultLocale) as Locale
  document.documentElement.lang = locale
  document.documentElement.dir = getDirection(locale)
}
syncHtmlAttrs(i18n.language)
i18n.on('languageChanged', syncHtmlAttrs)

export default i18n
